import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, User, FileText, BookOpen, Edit2, Check, X, Calendar, Trash2, MinusCircle } from 'lucide-react';
import api from '../../services/api';
import { useConfirmation } from '../../context/ConfirmationContext';

export default function AdminLecturerDetail() {
  const { lecturerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lecturer, setLecturer] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [exams, setExams] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { showConfirmation } = useConfirmation();

  useEffect(() => { loadData(); }, [lecturerId]);

  const loadData = async () => {
    try {
      // Try single-lecturer endpoint first
      const res = await api.get(`/admin/lecturers/${lecturerId}`);
      if (res.data && res.data.success) {
        // New API shape: { success: true, lecturer, subjects, exams }
        // Old PHP shape: { success: true, data: { lecturer, subjects, exams } }
        const top = res.data || {};
        const data = top.data || top; // prefer data branch if present
        const l = data.lecturer || top.lecturer || null;
        if (l) {
          const subjectsArr = data.subjects || top.subjects || l.subjects || [];
          const examsArr = data.exams || top.exams || l.exams || [];

          setLecturer(l);
          setFormData({
            full_name: l.full_name || '',
            email: l.email || '',
            phone_no: (l.phone_number ?? l.phone_no) || '',
            password: ''
          });
          setSubjects(Array.isArray(subjectsArr) ? subjectsArr : []);
          setExams(Array.isArray(examsArr) ? examsArr : []);
          return;
        }
      }

      // If not found or endpoint missing, fall back to fetching list and finding the lecturer
      console.warn('Falling back to /admin/lecturers list for lecturerId', lecturerId);
      const listRes = await api.get('/admin/lecturers');
      if (listRes.data && listRes.data.success) {
        console.debug('Lecturers list length:', (listRes.data.lecturers || []).length);
        const found = listRes.data.lecturers.find(l => parseInt(l.user_id) === parseInt(lecturerId));
        console.debug('Found lecturer from list:', found);
        if (found) {
          setLecturer(found);
          setFormData({
            full_name: found.full_name || '',
            email: found.email || '',
            phone_no: (found.phone_number ?? found.phone_no) || '',
            password: ''
          });
          setSubjects(found.subjects || []);
          setExams([]);
          return;
        }
      }

      setError('Lecturer not found');
    } catch (err) {
      // If single endpoint returned 404 or similar, try fallback
      if (err.response && err.response.status === 404) {
        try {
          const listRes = await api.get('/admin/lecturers');
          if (listRes.data && listRes.data.success) {
            const found = listRes.data.lecturers.find(l => l.user_id === parseInt(lecturerId));
            if (found) {
              setLecturer(found);
              setFormData({
                full_name: found.full_name || '',
                email: found.email || '',
                phone_no: found.phone_no || '',
                password: ''
              });
              setSubjects(found.subjects || []);
              setExams([]);
              return;
            }
          }
        } catch (err2) {
        }
      }

      setError(err.response?.data?.message || 'Failed to load lecturer details');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setError('');
      
      // Validation
      if (!formData.full_name.trim()) {
        setError('❌ Full name is required');
        return;
      }
      if (!formData.email.trim()) {
        setError('❌ Email is required');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('❌ Please enter a valid email address');
        return;
      }
      
      const updateData = {
        full_name: formData.full_name,
        email: formData.email,
        phone_no: formData.phone_no,
        password: formData.password
      };

      const res = await api.put(`/admin/lecturers/${lecturerId}`, updateData);
      if (res.data.success) {
        setSuccess('✅ Lecturer updated successfully!');
        setEditMode(false);
        loadData();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('❌ ' + (err.response?.data?.message || 'Failed to update lecturer'));
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setError('');
    setFormData({
      full_name: lecturer?.full_name || '',
      email: lecturer?.email || '',
      phone_no: lecturer?.phone_no || '',
      password: ''
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  if (error && !lecturer) {
    return (
      <div className="space-y-6">
        <button onClick={() => navigate('/admin/lecturers')} className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      </div>
    );
  }

  if (!lecturer) return <div>No lecturer found</div>;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button onClick={() => navigate('/admin/lecturers')} className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium">
        <ArrowLeft className="w-4 h-4" /> Back to Lecturers
      </button>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-green-700">{success}</div>
      )}

      {/* Lecturer Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
              <span className="text-2xl font-bold text-purple-600">{lecturer.full_name?.charAt(0)}</span>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">{lecturer.full_name}</h1>
              <p className="text-gray-500 mt-1">Lecturer</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setEditMode(!editMode)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 font-medium transition"
            >
              <Edit2 className="w-4 h-4" />
              {editMode ? 'Cancel' : 'Edit'}
            </button>
            <button
              onClick={() => showConfirmation({
                title: 'Delete Lecturer?',
                message: `Are you sure you want to delete "${lecturer.full_name}"? This action cannot be undone.`,
                type: 'danger',
                confirmText: 'Delete',
                cancelText: 'Cancel',
                onConfirm: async () => {
                  try {
                    const res = await api.delete(`/admin/lecturers/${lecturer.user_id}`);
                    if (res.data.success) {
                      navigate('/admin/lecturers');
                    }
                  } catch (err) {
                    setError(err.response?.data?.error || err.response?.data?.message || 'Failed to delete lecturer');
                  }
                }
              })}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 font-medium transition"
            >
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          </div>
        </div>
      </div>

      {/* Lecturer Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-md font-semibold text-gray-800">Assigned Subjects</h3>
          <p className="text-sm text-gray-500 mt-1">{subjects.length} subjects</p>
          <div className="mt-4">
            {subjects.length === 0 && <div className="text-gray-400 text-sm">No subjects assigned</div>}

            {/* Group subjects by grade and render per-grade cards */}
            {(() => {
              const grouped = subjects.reduce((acc, s) => {
                const g = s.grade_name || 'Ungraded';
                if (!acc[g]) acc[g] = [];
                acc[g].push(s);
                return acc;
              }, {});

              return Object.entries(grouped).map(([grade, subs]) => (
                <div key={grade} className="mb-4 bg-white rounded-lg border border-gray-100 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold text-gray-800">{grade}</div>
                    <div className="text-xs text-gray-500">{subs.length} subject{subs.length > 1 ? 's' : ''}</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {subs.map(s => (
                      <div key={s.subject_id} className="py-2 px-3 bg-gray-50 rounded">
                        <div className="font-medium text-gray-800">{s.subject_name}</div>
                        <div className="text-xs text-gray-500">{s.subject_code}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-md font-semibold text-gray-800">Recent Exams</h3>
          <p className="text-sm text-gray-500 mt-1">{exams.length} exams</p>
          <div className="mt-4">
            {exams.length === 0 && <div className="text-gray-400 text-sm">No exams created</div>}
            {exams.map(e => (
              <div key={e.exam_id} className="flex items-center justify-between py-2 border-b border-gray-100">
                <div>
                  <div className="font-medium text-gray-800">{e.exam_name}</div>
                  <div className="text-xs text-gray-500">{new Date(e.exam_date).toLocaleDateString()}</div>
                </div>
                <div className="text-sm text-gray-600">{e.total_marks} pts</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lecturer Details */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-6">Lecturer Information</h2>

        {editMode ? (
          <div className="space-y-6">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${!formData.full_name.trim() && error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-purple-500'}`}
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email <span className="text-red-500">*</span></label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${!formData.email.trim() && error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-purple-500'}`}
              />
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
              <input
                type="tel"
                name="phone_no"
                value={formData.phone_no}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password (optional - leave blank to keep current)</label>
              <input
                type="text"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter new password to change"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={handleSave}
                className="inline-flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition"
              >
                <Check className="w-4 h-4" /> Save Changes
              </button>
              <button
                onClick={handleCancel}
                className="inline-flex items-center gap-2 px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition"
              >
                <X className="w-4 h-4" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Display Mode */}
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <Mail className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium text-gray-800">{lecturer.email}</p>
              </div>
            </div>

            {(lecturer.phone_number || lecturer.phone_no) ? (
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <Phone className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Phone Number</p>
                  <p className="font-medium text-gray-800">{lecturer.phone_number ?? lecturer.phone_no}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <Phone className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Phone Number</p>
                  <p className="font-medium text-gray-400">Not registered</p>
                </div>
              </div>
            )}

            {lecturer.subjects && (
              <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-lg">
                <BookOpen className="w-5 h-5 text-purple-400" />
                <div>
                  <p className="text-sm text-purple-600">Assigned Subjects</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {lecturer.subjects.split(', ').map((s, i) => (
                      <span key={i} className="px-2 py-1 bg-indigo-100 text-indigo-600 rounded-full text-xs font-medium">{s}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {lecturer.is_active !== undefined && (
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <User className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className={`font-medium ${Number(lecturer.is_active) === 1 ? 'text-green-600' : 'text-red-600'}`}>
                    {Number(lecturer.is_active) === 1 ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>
            )}

            {lecturer.created_at && (
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Created</p>
                  <p className="font-medium text-gray-800">
                    {new Date(lecturer.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
